import { FC, useCallback, useEffect, useRef } from 'react';

import RazorpayCommon from 'components/razorpay-common';
import { useAppDispatch, useAppSelector } from 'hooks';
import loadRazorpay, { RazorpayWrapper } from 'lib/razorpay-checkout';
import { postProcessRenewal } from 'modules/cog';
import { showErrorSnack } from 'modules/error';
import { accountKey, sessionId } from 'modules/global-wrapper';
import { paymentMethodTypes, setPayNowIntent } from 'modules/payment-methods';
import {
  isEcomClient,
  isNewTabPayment,
  sessionClientURL,
  sessionCurrencyCode,
  sessionInvoiceNumber,
  sessionProducts,
  sessionQuoteId,
  sessionTotalAmount,
  sesssionCancelRedirectUrl,
} from 'modules/session-details';
import { GAEventNames, GATrack } from 'modules/tracking';
import { getPaymentTrackingType, getPurchaseFlow, trackRevenue } from 'modules/tracking/tracking-module';
import {
  addAndPayRazorpayStep1Resp,
  postTransactionAddAndPayRazorpayStep1,
  postTransactionAddAndPayRazorpayStep2,
  razorPayIsPending,
  razorPayIsSuccess,
  resetAddAndPayResponse,
} from 'modules/transaction-razorpay';
import { postProcessInvoice, postProcessQuote } from 'modules/ucs';
import { ONE_PAY_INTENT, PAYMENT_METHODS_TYPES, SEARCH_PARAMS } from 'utils/constants';
import st from 'utils/shared-translations';
import { getProductAdminDataFromCookie } from 'utils/ui-utils';

let RazorpayInstance: RazorpayWrapper | undefined = undefined;

const RazorPay: FC = () => {
  const dispatch = useAppDispatch();

  const isQuoteProccessing = useRef<boolean>(false);

  const renewalDate = localStorage.getItem(SEARCH_PARAMS.renewalDate) || '';

  const selectedAccountKey = useAppSelector(accountKey);

  const selectedSessionId = useAppSelector(sessionId);
  const selectedIsNewTabPayment = useAppSelector(isNewTabPayment);
  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const selectedQuoteKey = useAppSelector(sessionQuoteId);
  const selectedQuoteTotalPrice = useAppSelector(sessionTotalAmount);
  const selectedSessionProducts = useAppSelector(sessionProducts);
  const selectedSessionCurrencyCode = useAppSelector(sessionCurrencyCode);
  const selectedClientIsEcom = useAppSelector(isEcomClient);
  const selectedSessionClientUrl = useAppSelector(sessionClientURL);
  const selectedSessionCancelUrl = useAppSelector(sesssionCancelRedirectUrl);

  const selectedPaymentMethodTypes = useAppSelector(paymentMethodTypes);

  const selectedAddAndPayRpStep1Resp = useAppSelector(addAndPayRazorpayStep1Resp);
  const selectedRazorpayIsSuccess = useAppSelector(razorPayIsSuccess);
  const selectedRazorpayIsPending = useAppSelector(razorPayIsPending);

  const productAdminData = getProductAdminDataFromCookie();

  useEffect(() => {
    if (selectedAccountKey && selectedSessionId) {
      RazorpayInstance = loadRazorpay();
      dispatch(postTransactionAddAndPayRazorpayStep1())
        .unwrap()
        .catch(() => dispatch(showErrorSnack(st['alert.error.paymentmethod.general.failure'])));
    }
  }, [dispatch, selectedAccountKey, selectedSessionId]);

  const handlePaymentSuccess = (data) =>
    dispatch(postTransactionAddAndPayRazorpayStep2(data))
      .unwrap()
      .catch(() => dispatch(showErrorSnack(st['alert.error.paymentmethod.general.failure'])));

  const handleModalClose = () => {
    dispatch(setPayNowIntent(ONE_PAY_INTENT.addPayment));
    dispatch(resetAddAndPayResponse());
  };

  const processRenwal = useCallback(() => {
    const payload = {
      date: renewalDate,
      sessionId: selectedSessionId,
      amount: selectedQuoteTotalPrice.toString(),
    };
    dispatch(postProcessRenewal(payload))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: getPaymentTrackingType(PAYMENT_METHODS_TYPES.razorpay, selectedPaymentMethodTypes),
          quoteId: selectedQuoteKey,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      )
      .catch(() => {});
  }, [
    dispatch,
    renewalDate,
    selectedClientIsEcom,
    selectedQuoteKey,
    selectedQuoteTotalPrice,
    selectedSessionCancelUrl,
    selectedSessionClientUrl,
    selectedSessionCurrencyCode,
    selectedSessionId,
    selectedSessionProducts,
    selectedPaymentMethodTypes,
  ]);

  const processQuote = useCallback(() => {
    const data = {
      quoteCode: selectedQuoteKey,
      payload: {
        grossAmount: selectedQuoteTotalPrice,
        sessionId: selectedSessionId,
        paymentType: PAYMENT_METHODS_TYPES.razorpay,
        ...(Object.keys(productAdminData).length > 0 && { productAdmin: productAdminData }),
      },
    };
    dispatch(postProcessQuote(data))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: getPaymentTrackingType(PAYMENT_METHODS_TYPES.razorpay, selectedPaymentMethodTypes),
          quoteId: selectedQuoteKey,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      )
      .catch(() => {
        GATrack(GAEventNames.PurchaseError, {
          products: selectedSessionProducts,
          paymentMethod: PAYMENT_METHODS_TYPES.razorpay,
          errMsg: GAEventNames.PROVISIONING_FAILED,
        });
      });
  }, [
    dispatch,
    productAdminData,
    selectedClientIsEcom,
    selectedPaymentMethodTypes,
    selectedQuoteKey,
    selectedQuoteTotalPrice,
    selectedSessionCancelUrl,
    selectedSessionClientUrl,
    selectedSessionCurrencyCode,
    selectedSessionId,
    selectedSessionProducts,
  ]);

  const processInvoice = useCallback(() => {
    dispatch(postProcessInvoice({ sessionId: selectedSessionId }))
      .unwrap()
      .then(() =>
        trackRevenue({
          orderList: selectedSessionProducts,
          currencyCode: selectedSessionCurrencyCode,
          purchaseFlow: getPurchaseFlow(selectedClientIsEcom, selectedSessionCancelUrl),
          paymentMethod: getPaymentTrackingType(PAYMENT_METHODS_TYPES.razorpay, selectedPaymentMethodTypes),
          quoteId: selectedInvoiceNumber,
          clientURL: selectedSessionClientUrl,
          total: selectedQuoteTotalPrice,
        }),
      );
  }, [
    dispatch,
    selectedInvoiceNumber,
    selectedClientIsEcom,
    selectedPaymentMethodTypes,
    selectedQuoteTotalPrice,
    selectedSessionCancelUrl,
    selectedSessionClientUrl,
    selectedSessionCurrencyCode,
    selectedSessionId,
    selectedSessionProducts,
  ]);

  useEffect(() => {
    if ((selectedRazorpayIsSuccess || selectedRazorpayIsPending) && !isQuoteProccessing.current) {
      isQuoteProccessing.current = true;
      if (selectedIsNewTabPayment) window.close();
      else if (renewalDate) processRenwal();
      else if (selectedInvoiceNumber) processInvoice();
      else processQuote();
    }
  }, [
    processRenwal,
    processQuote,
    renewalDate,
    selectedRazorpayIsSuccess,
    selectedRazorpayIsPending,
    selectedIsNewTabPayment,
    selectedInvoiceNumber,
    processInvoice,
  ]);

  return (
    <RazorpayCommon
      Razorpay={RazorpayInstance}
      step1Resp={selectedAddAndPayRpStep1Resp}
      handlePaymentSuccess={handlePaymentSuccess}
      handleModalClose={handleModalClose}
    />
  );
};

export default RazorPay;
